import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Helmet } from 'react-helmet';
import { Loader2, Lock, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { COMPANY_NAME, COMPANY_RIF, COMPANY_SLOGAN, companyPageTitle } from '../utils/company.js';
import BrandLogo from '../components/BrandLogo.js';

const getLoginErrorMessage = (err) => {
  const apiError = err?.response?.data?.error;

  if (apiError === 'Invalid credentials') {
    return 'Credenciales incorrectas. Verifica tu usuario y contraseña.';
  }
  if (apiError === 'Please provide a username and password') {
    return 'Ingresa tu usuario y contraseña.';
  }
  if (apiError && /inactiva|inactive/i.test(apiError)) {
    return apiError;
  }
  if (apiError) return apiError;
  if (err?.response?.status === 401) {
    return 'Credenciales incorrectas. Verifica tu usuario y contraseña.';
  }
  return 'No se pudo iniciar sesión. Intenta de nuevo.';
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      toast.error(getLoginErrorMessage(err), { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      <Helmet>
        <title>{companyPageTitle("Iniciar Sesión")}</title>
      </Helmet>

      <div className="page-enter w-full max-w-[400px]">
        <div className="flex flex-col items-center text-center mb-8">
          <BrandLogo className="w-28 h-28 drop-shadow-md" alt={COMPANY_NAME} />
          <p className="font-bold text-gray-900 leading-tight mt-4">{COMPANY_NAME}</p>
          <p className="text-gray-400 text-xs mt-0.5">RIF {COMPANY_RIF}</p>
          <p className="text-indigo-600/80 text-xs font-medium mt-1">{COMPANY_SLOGAN}</p>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Bienvenido</h1>
          <p className="text-gray-500 text-sm mt-1.5">
            Ingresa tus credenciales para acceder al panel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 font-medium">
              Usuario
            </Label>
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <Input
                id="email"
                type="text"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Tu nombre de usuario"
                className="pl-9 h-11 bg-white border-gray-200 focus-visible:ring-indigo-500/30 focus-visible:border-indigo-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700 font-medium">
              Contraseña
            </Label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 h-11 bg-white border-gray-200 focus-visible:ring-indigo-500/30 focus-visible:border-indigo-400"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm shadow-indigo-200 transition-all"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Verificando...
              </>
            ) : (
              'Iniciar sesión'
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-8">
          {COMPANY_NAME} · RIF {COMPANY_RIF}
        </p>
      </div>
    </div>
  );
};

export default Login;
